import React, { useState, useEffect, useRef, memo } from 'react';
import useNumericKeypad from 'hooks/useNumericKeypad';

// An input that defers min/max clamping to blur instead of clamping during typing.
// During editing, only the internal display state is updated.
// On blur: parse, clamp, and propagate the value to the parent.
// On mobile (< 768px), focusing the field opens an on-screen numeric keypad
// instead of the native keyboard.
// parseType: 'int' or 'float'
const BoundedInput = ({ value, onChange, min, max, parseType = 'float', className, id, disabled }) => {
	const [displayValue, setDisplayValue] = useState(value ?? '');
	const isFocused = useRef(false);
	const openKeypad = useNumericKeypad();

	useEffect(() => {
		if (!isFocused.current) {
			setDisplayValue(value ?? '');
		}
	}, [value]);

	const parse = val => (parseType === 'int' ? parseInt(val, 10) : parseFloat(val));

	// Clamp only against the bounds that were actually provided. Guarding on
	// `typeof` avoids producing NaN when `min` or `max` is undefined
	// (Math.max(x, undefined) === NaN), which would otherwise propagate an
	// invalid value to the parent.
	const clamp = parsed => {
		let result = parsed;
		if (typeof min === 'number') {
			result = Math.max(result, min);
		}
		if (typeof max === 'number') {
			result = Math.min(result, max);
		}
		return result;
	};

	const handleChange = e => {
		setDisplayValue(e.target.value);
	};

	const handleFocus = async e => {
		isFocused.current = true;
		// On mobile, suppress the native keyboard and use the on-screen keypad instead.
		if (typeof window !== 'undefined' && window.innerWidth < 768) {
			e.preventDefault();
			e.currentTarget.blur();
			const result = await openKeypad({
				initialValue: value ?? '',
				min,
				max,
				allowDecimals: parseType !== 'int',
			});
			isFocused.current = false;
			if (typeof result === 'number' && !isNaN(result)) {
				const clamped = clamp(result);
				setDisplayValue(clamped);
				onChange(clamped);
			}
		}
	};

	const handleBlur = () => {
		isFocused.current = false;
		const raw = displayValue;
		if (raw === '' || raw === '-') {
			onChange(raw === '' ? '' : raw);
			return;
		}
		const parsed = parse(raw);
		if (isNaN(parsed)) {
			return;
		}
		const clamped = clamp(parsed);
		setDisplayValue(clamped);
		onChange(clamped);
	};

	return (
		<input
			type="text"
			id={id}
			value={displayValue}
			onChange={handleChange}
			onFocus={handleFocus}
			onBlur={handleBlur}
			className={className}
			disabled={disabled}
		/>
	);
};

export default memo(BoundedInput);
